/**
 * Streamable HTTP transport for MCP — the browser-friendly half of
 * the MCP transport spec. POSTs JSON-RPC requests to a single URL;
 * accepts either `application/json` (single response) or
 * `text/event-stream` (server-driven message stream) back.
 *
 * Just enough surface to support `initialize`, `tools/list`, and
 * `tools/call` — server-to-client streaming notifications during a
 * tool call are observed but not delivered to the caller (we wait
 * for the final response with our request id and then close). When
 * we want progress streaming for tool calls later, that goes here.
 */

export const MCP_PROTOCOL_VERSION = "2025-06-18";

export class StreamableHttpTransport {
  // `getToken` is awaited on every request so token refresh in
  // registry.js shows up automatically. `onUnauthorized` is invoked
  // once on a 401 to let the registry try a refresh + retry exactly
  // one more time — anything beyond that is the caller's problem.
  constructor({ baseUrl, getToken, onUnauthorized }) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
    this.onUnauthorized = onUnauthorized;
    this.sessionId = null;
    this.nextId = 1;
  }

  async request(method, params, { signal } = {}) {
    const id = this.nextId++;
    const body = { jsonrpc: "2.0", id, method, params };
    const msg = await this._send(body, { signal, expectResponse: true });
    if (!msg) throw new Error(`MCP ${method} returned no response`);
    if (msg.error) {
      const err = new Error(msg.error.message || `MCP error ${msg.error.code}`);
      err.code = msg.error.code;
      err.data = msg.error.data;
      throw err;
    }
    return msg.result;
  }

  async notify(method, params) {
    const body = { jsonrpc: "2.0", method, params };
    await this._send(body, { expectResponse: false });
  }

  async _send(jsonRpcBody, { signal, expectResponse }) {
    const headers = await this._headers();
    const requestId = jsonRpcBody.id;
    let res;
    try {
      res = await fetch(this.baseUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(jsonRpcBody),
        signal,
      });
    } catch (err) {
      throw new Error(`MCP fetch failed: ${err.message || err}`);
    }

    if (res.status === 401 && this.onUnauthorized) {
      const retryToken = await this.onUnauthorized();
      if (retryToken) {
        const retryHeaders = { ...headers, Authorization: `Bearer ${retryToken}` };
        res = await fetch(this.baseUrl, {
          method: "POST",
          headers: retryHeaders,
          body: JSON.stringify(jsonRpcBody),
          signal,
        });
      }
    }

    const sid = res.headers.get("Mcp-Session-Id");
    if (sid) this.sessionId = sid;

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`MCP HTTP ${res.status}: ${text || res.statusText}`);
    }

    if (!expectResponse || res.status === 202) {
      // Drain the body so the connection can be reused.
      try { await res.text(); } catch {}
      return null;
    }

    const contentType = res.headers.get("Content-Type") || "";
    if (contentType.includes("application/json")) {
      return res.json();
    }
    if (contentType.includes("text/event-stream")) {
      return this._readSseUntil(res, requestId, signal);
    }
    // Some servers reply with an empty body even when they should not.
    // Tolerate it rather than blow up.
    const text = await res.text().catch(() => "");
    if (!text) return null;
    try { return JSON.parse(text); } catch {
      throw new Error(`Unexpected MCP content-type "${contentType}" with body: ${text.slice(0, 120)}`);
    }
  }

  async _headers() {
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json, text/event-stream",
      "MCP-Protocol-Version": MCP_PROTOCOL_VERSION,
    };
    const token = await this.getToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (this.sessionId) headers["Mcp-Session-Id"] = this.sessionId;
    return headers;
  }

  // Read the SSE body until we find a JSON-RPC message whose `id`
  // matches the request we're waiting on, then bail out. Notifications
  // and progress events along the way are observed (parsed so a
  // malformed payload still surfaces) but not delivered upstream —
  // wire those through later if/when the chat needs progress.
  async _readSseUntil(res, requestId, signal) {
    if (!res.body) throw new Error("MCP SSE response has no body");
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    const onAbort = () => {
      try { reader.cancel(); } catch {}
    };
    signal?.addEventListener("abort", onAbort);
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf("\n\n")) !== -1) {
          const eventBlock = buf.slice(0, nl);
          buf = buf.slice(nl + 2);
          const data = parseSseData(eventBlock);
          if (!data) continue;
          let msg;
          try { msg = JSON.parse(data); } catch { continue; }
          if (Array.isArray(msg)) {
            const hit = msg.find((m) => m && m.id === requestId);
            if (hit) {
              try { reader.cancel(); } catch {}
              return hit;
            }
          } else if (msg && msg.id === requestId) {
            try { reader.cancel(); } catch {}
            return msg;
          }
          // Otherwise: server-to-client notification or unrelated message.
        }
      }
    } finally {
      signal?.removeEventListener("abort", onAbort);
    }
    return null;
  }
}

function parseSseData(block) {
  // SSE events look like `event: x\ndata: y\ndata: z`. Concatenate
  // all `data:` lines (with newlines between) per the spec; we only
  // care about message events for MCP.
  const lines = block.split("\n");
  const dataLines = [];
  for (const raw of lines) {
    if (raw.startsWith(":")) continue; // comment
    if (raw.startsWith("data:")) {
      // The spec allows `data: foo` with a leading space; strip one.
      const v = raw.slice(5);
      dataLines.push(v.startsWith(" ") ? v.slice(1) : v);
    }
  }
  return dataLines.length ? dataLines.join("\n") : null;
}
